"use client";

import React from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { ArrowRight, ChartBar, Globe, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "@/components/common/Link";
const tradeData = [{
  country: "Germany",
  exports: 14520,
  imports: 12280,
  total: 26800
}, {
  country: "Netherlands",
  exports: 8950,
  imports: 15410,
  total: 24360
}, {
  country: "France",
  exports: 7240,
  imports: 6890,
  total: 14130
}, {
  country: "Italy",
  exports: 6580,
  imports: 5920,
  total: 12500
}, {
  country: "Belgium",
  exports: 5810,
  imports: 6140,
  total: 11950
}, {
  country: "Spain",
  exports: 3490,
  imports: 4210,
  total: 7700
}];
const chartConfig = {
  exports: {
    label: "Exports to India (€M)",
    color: "var(--chart-1)",
    icon: TrendingUp
  },
  imports: {
    label: "Imports from India (€M)",
    color: "var(--chart-2)",
    icon: Globe
  }
} satisfies ChartConfig;
export default function EuropeanTradeCorridorInsights() {
  return <section id="europeantradecorridorinsights" className="w-full py-16 md:py-24 bg-background text-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <Badge variant="outline" className="mb-4 border-primary/20 text-primary bg-primary/5">
              <ChartBar className="w-3 h-3 mr-1" />
              Market Data 2023-2024
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-primary mb-4">
              European Trade Corridor Insights
            </h2>
            <p className="text-muted-foreground text-lg">
              Analyzing the bilateral trade volume between key European Union member states and India. EuroIndiaTrade.com facilitates connections across these high-growth corridors.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild variant="outline" className="border-border hover:bg-muted">
              <Link to="/MarketEntryPage">
                Explore Market Entry
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {}
          <Card className="lg:col-span-5 border-border shadow-sm bg-card/50 backdrop-blur-sm">
            <CardHeader className="border-b border-border pb-4">
              <CardTitle className="text-lg text-primary">Trade Volume by Nation</CardTitle>
              <CardDescription>Figures in Millions of Euros (€M)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-muted/30">
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="w-[140px] text-primary font-semibold">EU Nation</TableHead>
                    <TableHead className="text-right text-primary font-semibold">Exports</TableHead>
                    <TableHead className="text-right text-primary font-semibold">Imports</TableHead>
                    <TableHead className="text-right text-primary font-semibold">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tradeData.map((row, i) => <TableRow key={row.country} data-index={i} className="border-border hover:bg-muted/50 transition-colors">
                      <TableCell className="font-medium text-foreground">
                        {row.country}
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {row.exports.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {row.imports.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-primary">
                        {row.total.toLocaleString()}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {}
          <Card className="lg:col-span-7 border-border shadow-sm bg-card/50 backdrop-blur-sm">
            <CardHeader className="border-b border-border pb-4">
              <CardTitle className="text-lg text-primary">Export vs Import Comparison</CardTitle>
              <CardDescription>Visualizing the trade balance across top EU partners</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <ChartContainer config={chartConfig} className="h-[350px] w-full">
                <BarChart data={tradeData} margin={{
                top: 20,
                right: 0,
                left: -20,
                bottom: 0
              }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.5} />
                  <XAxis dataKey="country" tickLine={false} axisLine={false} tickMargin={10} className="text-xs font-medium fill-muted-foreground" />
                  <YAxis tickLine={false} axisLine={false} tickFormatter={value => `€${value}`} className="font-mono text-xs fill-muted-foreground" />
                  <ChartTooltip cursor={{
                  fill: 'var(--muted)',
                  opacity: 0.4
                }} content={<ChartTooltipContent indicator="dot" />} />
                  <ChartLegend content={<ChartLegendContent />} />
                  <Bar dataKey="exports" fill="var(--color-exports)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  <Bar dataKey="imports" fill="var(--color-imports)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>

        </div>
      </div>
    </section>;
}